import React, { useState } from 'react';
import { UserProfile } from '../types';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Rocket, ChevronLeft } from 'lucide-react';
import { isSystemAdmin } from '../lib/admin';
import { doc, setDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { signOut } from 'firebase/auth';
import { toast } from 'sonner';

interface RegistrationFormProps {
  user: any;
  onRegistered: (profile: UserProfile) => void;
}

export default function RegistrationForm({ user, onRegistered }: RegistrationFormProps) {
  const [formData, setFormData] = useState({
    firstName: user.displayName?.split(' ')[0] || '',
    lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
    email: user.email || '',
    sccNumber: '',
    shipName: '',
    cell: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Check if there is an existing pre-imported profile with verified user.email or sfiEmail
      let preImportedProfile: any = null;
      let preImportedDocId: string | null = null;

      try {
        const verifiedEmail = (user?.email || '').trim().toLowerCase();
        const qEmail = query(collection(db, 'users'), where('email', '==', verifiedEmail));
        const qSfiEmail = query(collection(db, 'users'), where('sfiEmail', '==', verifiedEmail));
        
        const [emailSnap, sfiEmailSnap] = await Promise.all([
          getDocs(qEmail),
          getDocs(qSfiEmail)
        ]);
        
        if (!emailSnap.empty) {
          const docSnap = emailSnap.docs[0];
          preImportedProfile = docSnap.data();
          preImportedDocId = docSnap.id;
        } else if (!sfiEmailSnap.empty) {
          const docSnap = sfiEmailSnap.docs[0];
          preImportedProfile = docSnap.data();
          preImportedDocId = docSnap.id;
        }
      } catch (lookupErr) {
        console.warn('Could not query existing profiles by email:', lookupErr);
      }

      const newProfile: UserProfile = {
        firstName: formData.firstName || preImportedProfile?.firstName || '',
        lastName: formData.lastName || preImportedProfile?.lastName || '',
        email: user?.email || formData.email,
        sfiEmail: preImportedProfile?.sfiEmail || user?.email || formData.email,
        sccNumber: formData.sccNumber || preImportedProfile?.sccNumber || '',
        shipName: formData.shipName || preImportedProfile?.shipName || '',
        cell: formData.cell || preImportedProfile?.cell || '',
        dob: preImportedProfile?.dob || '',
        sfiJoinDate: preImportedProfile?.sfiJoinDate || '',
        sfiExpireDate: preImportedProfile?.sfiExpireDate || '',
        zavalaJoinDate: preImportedProfile?.zavalaJoinDate || new Date().toISOString().split('T')[0],
        zavalaExpireDate: preImportedProfile?.zavalaExpireDate || '',
        ots: preImportedProfile?.ots || false,
        occ: preImportedProfile?.occ || false,
        sdfpp: preImportedProfile?.sdfpp || false,
        roles: (
          isSystemAdmin(user?.email)
            ? ['admin']
            : (preImportedProfile?.roles?.filter((r: string) => r !== 'admin' && r !== 'user_manager') || 
               ((formData.shipName || preImportedProfile?.shipName || '').toLowerCase().includes('zavala') ? ['member'] : ['read_only']))
        ),
        manualPoints: preImportedProfile?.manualPoints !== undefined ? preImportedProfile.manualPoints : 0,
        promotionRankDate: preImportedProfile?.promotionRankDate || new Date().toISOString().split('T')[0],
        currentRank: preImportedProfile?.currentRank || 'CRMN',
        uid: user.uid,
        status: 'active'
      };

      const docRef = doc(db, 'users', user.uid);
      await setDoc(docRef, newProfile);
      
      // If we adopted a pre-imported profile that was stored under a different document ID, attempt to clean it up
      if (preImportedDocId && preImportedDocId !== user.uid) {
        try {
          await deleteDoc(doc(db, 'users', preImportedDocId));
        } catch (delErr) {
          console.warn('Non-fatal: could not delete placeholder profile doc', delErr);
        }
      }
      
      toast.success('Registration complete. Welcome to the USS Zavala!');
      onRegistered(newProfile);
    } catch (error: any) {
      console.error('Registration failed:', error);
      toast.error('Registration failed: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[var(--background)] flex flex-col items-center justify-center p-4 lcars-bg text-lcars-gold">
      <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-center gap-3 mb-8 relative">
          <button 
            type="button" 
            onClick={handleCancel}
            className="absolute left-0 p-2 text-lcars-blue hover:text-[var(--foreground)] transition-colors"
            title="Cancel and Sign Out"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <Rocket className="w-10 h-10 text-lcars-blue" />
          <h1 className="text-3xl font-bold uppercase tracking-widest text-lcars-blue">Enlistment</h1>
        </div>

        <Card className="bg-[var(--background)]/80 border-lcars-tan/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-lcars-tan uppercase tracking-widest text-center">New Personnel Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-lcars-blue uppercase text-xs font-bold tracking-wider">First Name</Label>
                  <Input 
                    id="firstName"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="bg-[var(--background)]/50 border-lcars-blue/50 text-[var(--foreground)] rounded-none focus:border-lcars-gold"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-lcars-blue uppercase text-xs font-bold tracking-wider">Last Name</Label>
                  <Input 
                    id="lastName"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="bg-[var(--background)]/50 border-lcars-blue/50 text-[var(--foreground)] rounded-none focus:border-lcars-gold"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="email" className="text-lcars-blue uppercase text-xs font-bold tracking-wider">Email Address</Label>
                  <span className="text-[0.65rem] text-lcars-gold/70 font-mono tracking-wider">VERIFIED BIOMETRIC ID</span>
                </div>
                <Input 
                  id="email"
                  type="email"
                  required
                  disabled
                  readOnly
                  value={user?.email || formData.email}
                  className="bg-[var(--background)]/20 border-lcars-blue/30 text-[var(--foreground)]/60 rounded-none cursor-not-allowed select-none"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cell" className="text-lcars-blue uppercase text-xs font-bold tracking-wider">Phone Number (Optional)</Label>
                <Input 
                  id="cell"
                  type="tel"
                  value={formData.cell}
                  onChange={(e) => setFormData({...formData, cell: e.target.value})}
                  className="bg-[var(--background)]/50 border-lcars-blue/50 text-[var(--foreground)] rounded-none focus:border-lcars-gold"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sccNumber" className="text-lcars-blue uppercase text-xs font-bold tracking-wider">SCC ID (Optional)</Label>
                <Input 
                  id="sccNumber"
                  value={formData.sccNumber}
                  onChange={(e) => setFormData({...formData, sccNumber: e.target.value})}
                  className="bg-[var(--background)]/50 border-lcars-blue/50 text-[var(--foreground)] rounded-none focus:border-lcars-gold"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shipName" className="text-lcars-blue uppercase text-xs font-bold tracking-wider">Ship Name (Optional)</Label>
                <Input 
                  id="shipName"
                  value={formData.shipName}
                  onChange={(e) => setFormData({...formData, shipName: e.target.value})}
                  placeholder="e.g. USS Zavala"
                  className="bg-[var(--background)]/50 border-lcars-blue/50 text-[var(--foreground)] rounded-none focus:border-lcars-gold"
                />
              </div>

              <div className="pt-4">
                <p className="text-[0.65rem] text-[var(--foreground)]/60 text-center mb-4 uppercase tracking-widest px-2">
                  Your personal information will not be shared without your consent
                </p>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full lcars-pill bg-lcars-purple text-black font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all h-12"
                >
                  {isSubmitting ? 'PROCESSING...' : 'SUBMIT REQUEST'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
